import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { delay, finalize, tap } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { IconKey } from '../../../../../whitelabel/icon-key.enum';
import { AuthService } from '../../../../core/services/auth.service';
import { LanguageService } from '../../../../core/services/language.service';
import { ButtonComponent } from '../../../../osp-ui/components/button/button/button.component';
import {
  CardOptionComponent,
} from '../../../../osp-ui/components/form/card-option-list/components/card-option/card-option.component';
import {
  TimefencingSettingsComponent,
} from '../../../../osp-ui/components/form/timefencing-settings/timefencing-settings.component';
import { StepperComponent } from '../../../../osp-ui/components/panel/stepper/stepper.component';
import { StepperModule } from '../../../../osp-ui/components/panel/stepper/stepper.module';
import { ViewGlobalGeofence } from '../../../../shared/contract/view-global-geofence.contract';
import { WeekDayConfiguration } from '../../../../shared/contract/week-day-configuration.contract';
import { Authorities } from '../../../../shared/enums/authorities.enum';
import { FieldLimit } from '../../../../shared/enums/field-limit.enum';
import { GeofenceAlarmTrigger } from '../../../../shared/enums/geofence-alarm-trigger.enum';
import { IntervalsDuration } from '../../../../shared/enums/intervals-duration.enum';
import { Modules } from '../../../../shared/enums/modules.enum';
import {
  EquipmentTypesGroupedSelectOption,
} from '../../../../shared/filters/contract/equipment-types-grouped-select-option.contract';
import { IconPipe } from '../../../../shared/pipes/icon.pipe';
import { DatesService } from '../../../../shared/services/dates.service';
import { DimensionSystemService } from '../../../../shared/services/dimension-system.service';
import { EquipmentTypeMergeService } from '../../../../shared/services/equipment-type-merge.service';
import { GlobalGeofencesDataSource } from '../../../../shared/services/global-geofence-datasource.service';
import { ToastService } from '../../../../shared/services/toast.service';
import compareCaseInsensitive from '../../../../shared/utils/compare-case-insensitive';
import { ControlFilteredOptionLoader } from '../../../../shared/utils/control-filtered-option-loader';
import { TelematicsUnitType } from '../../../resources/equipment/enums/telematics-unit-type.enum';
import { TELEMATICS_UNITS_TYPES_WITHOUT_BEACONS } from '../../constants/telematics-unit-types-without-beacons.const';
import { AlarmSettingsFieldData } from '../../contract/alarm-settings-field-data.contract';
import { AlarmTypeInfo } from '../../contract/alarm-type-info.contract';
import { CreateAlarmRuleCommand } from '../../contract/create-alarm-rule-command.contract';
import { Customer } from '../../contract/customer.contract';
import { UpdateAlarmRuleCommand } from '../../contract/update-alarm-rule-command.contract';
import { ViewAlarmRule } from '../../contract/view-alarm-rule.contract';
import { AlarmLevel } from '../../enums/alarm-level.enum';
import { AlarmRuleControl } from '../../enums/alarm-rule-control.enum';
import { AlarmSettingsType } from '../../enums/alarm-settings-type.enum';
import { AlarmType } from '../../enums/alarm-type.enum';
import { EquipmentAlarmRuleSelectionType } from '../../enums/equipment-alarm-rule-selection-type.enum';
import { TelematicsSelector } from '../../enums/telematics-selector.enum';
import { AlarmTypeDescriptionPipe } from '../../pipes/alarm-type-description.pipe';
import { AlarmTypeIconPipe } from '../../pipes/alarm-type-icon.pipe';
import { AlarmTypeResolverPipe } from '../../pipes/alarm-type-resolver.pipe';
import { AlarmSettingsUtilsService } from '../../services/alarm-settings-utils.service';
import { AlarmTypesService } from '../../services/alarm-types.service';
import { NotificationsDatasourceService } from '../../services/notifications-datasource.service';
import { EquipmentControlFilteredOptionLoader } from '../../utils/equipment-control-filtered-option-loader';
import { AdditionalDataComponent } from '../additional-data/additional-data.component';
import { GeofenceSettingsComponent } from '../alarm-settings/geofence-settings/geofence-settings.component';
import {
  InactiveTelematicUnitSettingsComponent,
} from '../alarm-settings/inactive-telematic-unit-settings/inactive-telematic-unit-settings.component';
import {
  LimitPercentageSettingsComponent,
} from '../alarm-settings/limit-percentage-settings/limit-percentage-settings.component';
import { LimitSettingsComponent } from '../alarm-settings/limit-settings/limit-settings.component';
import {
  TrackerBatteryStatusAlarmComponent,
} from '../alarm-settings/tracker-battery-percentage/tracker-battery-status.component';
import { AlarmTypeSelectionComponent } from '../alarm-type-selection/alarm-type-selection.component';
import { NotificationApiService } from '../../services/notification-api.service';
import { AlarmTypeConfig } from '../../contract/alarm-type-config.contract';


type NotificationForm = {
  [AlarmRuleControl.ALARM_TYPE]: FormControl<AlarmType | null>;
  [AlarmRuleControl.SETTINGS]: FormGroup<{
    [AlarmRuleControl.SETTINGS_LIMIT]: FormControl<number | null>;
    [AlarmRuleControl.SETTINGS_LIMIT_UNIT]: FormControl<IntervalsDuration | null>;
    [AlarmRuleControl.SETTINGS_INTERVAL]: FormControl<number | null>;
    [AlarmRuleControl.SETTINGS_GLOBAL_GEOFENCE]: FormControl<string | null>;
    [AlarmRuleControl.SETTINGS_GEOFENCE_TRIGGER]: FormControl<GeofenceAlarmTrigger | null>;
    [AlarmRuleControl.SETTINGS_TIMEFENCING_HOURS]: FormControl<number | null>;
    [AlarmRuleControl.SETTINGS_TIMEFENCING_DISTANCE]: FormControl<number | null>;
    [AlarmRuleControl.SETTINGS_TIMEFENCING_WEEK_DAY]: FormControl<WeekDayConfiguration | null>;
    [AlarmRuleControl.SETTINGS_TELEMATICS_SELECTOR]: FormControl<TelematicsSelector | null>;
    [AlarmRuleControl.SETTINGS_TELEMATICS_TYPES]: FormControl<TelematicsUnitType[] | null>;
    [AlarmRuleControl.SETTINGS_TELEMATICS_BATTERY_STATUS]: FormControl<string | null>;
  }>;
  [AlarmRuleControl.DATA]: FormGroup<{
    [AlarmRuleControl.DATA_ALARM_MESSAGE]: FormControl<string | null>;
    [AlarmRuleControl.DATA_ALARM_LEVEL]: FormControl<AlarmLevel | null>;
    [AlarmRuleControl.DATA_SELECTION_TYPE]: FormControl<EquipmentAlarmRuleSelectionType | null>;
    [AlarmRuleControl.DATA_LABELS]: FormControl<string[] | null>;
    [AlarmRuleControl.DATA_EQUIPMENT_TYPES]: FormControl<string[] | null>;
    [AlarmRuleControl.DATA_EQUIPMENTS]: FormControl<string[] | null>;
    [AlarmRuleControl.DATA_CUSTOMER]: FormControl<string[] | null>;
    [AlarmRuleControl.DATA_EMAIL_TOGGLE]: FormControl<boolean | null>;
    [AlarmRuleControl.DATA_EMAIL_RECIPIENTS]: FormControl<string[] | null>;
    [AlarmRuleControl.DATA_DEFAULT_EMAIL_RECIPIENTS]: FormControl<string[] | null>;
    [AlarmRuleControl.DATA_ROLE_RECIPIENTS]: FormControl<string[] | null>;
  }>;
}

interface Settings {
  limit?: number;
  limitDurationUnit?: string;
  interval: number;
  globalGeofence: string;
  geofenceAlarmTrigger: GeofenceAlarmTrigger;
  timefencingHoursLimit: number;
  timefencingDistanceLimit: number;
  timefencingWeekDay: WeekDayConfiguration;
  telematicsSelector: string;
  telematicsTypes: TelematicsUnitType[];
  trackerBatteryStatus: string;
}

interface Data {
  alarmMessage: AlarmRuleControl.DATA_ALARM_MESSAGE;
  alarmLevel: AlarmLevel;
  selectionType: EquipmentAlarmRuleSelectionType;
  labels?: string[];
  equipmentTypes: string[];
  equipments?: string[];
  customer?: string[];
  emailToggle: boolean;
  emailRecipients: string[];
  defaultEmailRecipients: string[];
  roleRecipients: string[];
}

interface FormData {
  alarmType: AlarmType;
  settings: Settings;
  data: Data;
}

@Component({
  selector: 'osp-notification-form',
  standalone: true,
  imports: [
    TranslateModule,
    ButtonComponent,
    ReactiveFormsModule,
    AdditionalDataComponent,
    LimitSettingsComponent,
    LimitPercentageSettingsComponent,
    InactiveTelematicUnitSettingsComponent,
    TrackerBatteryStatusAlarmComponent,
    GeofenceSettingsComponent,
    TimefencingSettingsComponent,
    AlarmTypeSelectionComponent,
    StepperModule,
    CardOptionComponent,
    AlarmTypeIconPipe,
    AlarmTypeResolverPipe,
    AlarmTypeDescriptionPipe,
    NgTemplateOutlet,
    IconPipe,
  ],
  templateUrl: './notification-form.component.html',
  styleUrl: './notification-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationFormDialogComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private equipmentTypeMergeService = inject(EquipmentTypeMergeService);

  @ViewChild(StepperComponent) stepper!: StepperComponent;

  private readonly SECOND_STEP_INDEX = 1;
  readonly IconKey = IconKey;

  protected alarm: ViewAlarmRule | null = null;
  protected allowedAlarmTypes: Map<AlarmType, AlarmTypeInfo> = new Map();
  protected labels: string[] = [];
  protected $equipmentTypes = signal<EquipmentTypesGroupedSelectOption[]>([]);
  protected globalGeofences: ViewGlobalGeofence[] = [];
  protected form!: FormGroup<NotificationForm>;
  protected $currentSettingsType = signal<AlarmSettingsType | null>(null);
  protected $currentAlarmType = signal<AlarmType | null>(null);
  protected $isSubmittingForm = signal(false);

  protected readonly controlNames = AlarmRuleControl;
  protected readonly settingsTypes = AlarmSettingsType;
  protected readonly alarmType = AlarmType;
  protected alarmTypeConfigs: AlarmTypeConfig[] = [];

  protected equipmentOptionLoader = new EquipmentControlFilteredOptionLoader();
  protected customerOptionLoader = new ControlFilteredOptionLoader<Customer, string>();


  constructor(
    private languageService: LanguageService,
    private authService: AuthService,
    private toastService: ToastService,
    private notificationsDatasource: NotificationsDatasourceService,
    private alarmTypesService: AlarmTypesService,
    protected alarmSettingsUtilsService: AlarmSettingsUtilsService,
    private dimensionSystemService: DimensionSystemService,
    protected dialogRef: DynamicDialogRef,
    protected dialogConfig: DynamicDialogConfig,
    private cdr: ChangeDetectorRef,
    private geofenceDatasource: GlobalGeofencesDataSource,
    private notificationApiService: NotificationApiService,
  ) { }

  ngOnInit(): void {
    this.alarm = this.dialogConfig.data;

    this.buildForm();
    this.getAllowedAlarmTypes();
    this.getLabels();
    this.getEquipmentTypes();
    this.getGlobalGeofences();

    this.initOptionLoaders();

    if (this.allowedToSeeDefaultAlarms()) {
      this.fetchTypeConfigs();
    }

    if (this.alarm) {
      this.patchFormToEdit();
      this.alarmTypeChanged(this.alarm.alarmType);
    }

    this.initAlarmTypeListener();
    this.$currentSettingsType.set(this.alarmSettingsUtilsService.getAlarmSettingsType());
  }

  protected get alarmTypeControl(): AbstractControl | null | undefined {
    return this.form.get(this.controlNames.ALARM_TYPE);
  }

  protected get settingsForm(): FormGroup {
    return this.form.get(this.controlNames.SETTINGS) as FormGroup;
  }

  protected get dataForm(): FormGroup {
    return this.form.get(this.controlNames.DATA) as FormGroup;
  }

  protected isValid(): boolean {
    return this.form.valid;
  }

  protected get alarmSettingsFieldData(): AlarmSettingsFieldData {
    return this.alarmSettingsUtilsService.getAlarmSettingsFieldData();
  }

  protected get isEditMode(): boolean {
    return Boolean(this.alarm);
  }

  protected handleSubmission(): void {
    if (this.isValid()) {
      this.isEditMode ? this.updateAlarmRule() : this.createAlarmRule();
    }
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected handleAlarmTypeChange(alarmType: AlarmType) {
    this.equipmentOptionLoader.alarmTypeChange(alarmType);
    this.stepper.updateActiveStep(this.SECOND_STEP_INDEX);
  }

  protected allowedToSeeDefaultAlarms() {
    return this.authService.hasAuthority(Authorities.NOTIFICATIONS_MANAGE_DEFAULT_RECIPIENTS);
  }

  protected buildDefaultRecipientsForAlarm() {
    const defaultRecipientsFromAlarmTypeConfig: string[] = this.alarmTypeConfigs
    .find(config => this.alarmTypeControl?.value == config.alarmType)
      ?.defaultEmailRecipients || [];

    let defaultRecipients;
    if (this.isEditMode) {
      defaultRecipients = this.alarm?.defaultEmailRecipientsFilledWithInfo.map(r => r.userId);
    } else {
      defaultRecipients = defaultRecipientsFromAlarmTypeConfig;
    }
    return defaultRecipients ?? [];
  }

  private getAllowedAlarmTypes() {
    this.alarmTypesService
      .getAllowedAlarmTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(allowedAlarmTypes => {
        this.allowedAlarmTypes = new Map(
          allowedAlarmTypes.map((type) => [type.type, type]),
        );
        this.form.updateValueAndValidity();
        this.cdr.markForCheck();
      }
      );
  }

  private getLabels(): void {
    this.notificationsDatasource
      .getDistinctLabels()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((labels) => {
        this.labels = [...labels].sort(compareCaseInsensitive);
        this.cdr.markForCheck();
      });
  }

  private getEquipmentTypes(): void {
    this.notificationsDatasource
      .getDistinctEquipmentTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((equipmentTypes) => {
        const mergedOptions = this.equipmentTypeMergeService.toGroupedSelectOptions(equipmentTypes);
        this.$equipmentTypes.set(mergedOptions);
        this.patchEquipmentTypesOnInit();
      });
  }

  private getGlobalGeofences(): void {
    if (
      this.authService.hasModule(Modules.LIVE_DATA) &&
      this.authService.hasAnyAuthority([Authorities.GEOFENCE_VIEW, Authorities.GEOFENCE_MANAGE])
    ) {
      this.geofenceDatasource.geofences$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res: ViewGlobalGeofence[]) => {
          this.globalGeofences = res.sort(({ globalGeofenceName: s1 }, { globalGeofenceName: s2 }) =>
            s1.localeCompare(s2, undefined, { sensitivity: 'base' }),
          );
          this.cdr.markForCheck();
        });
      this.geofenceDatasource.getGlobalGeofences();
    }
  }

  private buildForm(): void {
    this.form = new FormGroup<NotificationForm>({
      [this.controlNames.ALARM_TYPE]: new FormControl<AlarmType | null>(null, Validators.required),
      [this.controlNames.SETTINGS]: new FormGroup({
        [this.controlNames.SETTINGS_LIMIT]: new FormControl<number | null>(null, Validators.min(0)),
        [this.controlNames.SETTINGS_LIMIT_UNIT]: new FormControl<IntervalsDuration | null>(null),
        [this.controlNames.SETTINGS_INTERVAL]: new FormControl<number | null>(null),
        [this.controlNames.SETTINGS_GLOBAL_GEOFENCE]: new FormControl<string | null>(null),
        [this.controlNames.SETTINGS_GEOFENCE_TRIGGER]: new FormControl<GeofenceAlarmTrigger | null>(null),
        [this.controlNames.SETTINGS_TIMEFENCING_HOURS]: new FormControl<number | null>(null),
        [this.controlNames.SETTINGS_TIMEFENCING_DISTANCE]: new FormControl<number | null>(null),
        [this.controlNames.SETTINGS_TIMEFENCING_WEEK_DAY]: new FormControl<WeekDayConfiguration | null>(null),
        [this.controlNames.SETTINGS_TELEMATICS_SELECTOR]: new FormControl<TelematicsSelector | null>(null),
        [this.controlNames.SETTINGS_TELEMATICS_TYPES]: new FormControl<TelematicsUnitType[] | null>(null),
        [this.controlNames.SETTINGS_TELEMATICS_BATTERY_STATUS]: new FormControl<string | null>(null),
      }),
      [this.controlNames.DATA]: new FormGroup({
        [this.controlNames.DATA_ALARM_MESSAGE]: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(FieldLimit.LONG_TEXT)]),
        [this.controlNames.DATA_ALARM_LEVEL]: new FormControl<AlarmLevel | null>(null, Validators.required),
        [this.controlNames.DATA_SELECTION_TYPE]: new FormControl<EquipmentAlarmRuleSelectionType | null>(null, Validators.required),
        [this.controlNames.DATA_LABELS]: new FormControl<string[] | null>(null),
        [this.controlNames.DATA_EQUIPMENT_TYPES]: new FormControl<string[] | null>(null),
        [this.controlNames.DATA_EQUIPMENTS]: new FormControl<string[] | null>(null),
        [this.controlNames.DATA_CUSTOMER]: new FormControl<string[] | null>(null),
        [this.controlNames.DATA_EMAIL_TOGGLE]: new FormControl<boolean | null>(null),
        [this.controlNames.DATA_EMAIL_RECIPIENTS]: new FormControl<string[] | null>(null),
        [this.controlNames.DATA_DEFAULT_EMAIL_RECIPIENTS]: new FormControl<string[] | null>(null),
        [this.controlNames.DATA_ROLE_RECIPIENTS]: new FormControl<string[] | null>(null),
      }),
    });
  }

  private initAlarmTypeListener(): void {
    this.alarmTypeControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type: AlarmType) => this.alarmTypeChanged(type));
  }

  private initOptionLoaders(): void {
    this.equipmentOptionLoader.applyConfigs({
      control: this.form.controls.data.controls.equipments,
      optionKeyField: 'equipmentId',
      initialOptions: this.alarm?.equipments?.map(({ id, name }) => ({ equipmentId: id, name })) ?? [],
      requester: ({ term, excludeShared }) => {
        return this.notificationsDatasource.getEquipmentOptions(term, excludeShared);
      }
    });
    this.equipmentOptionLoader.searchChange('');

    this.customerOptionLoader.applyConfigs({
      control: this.form.controls.data.controls.customer,
      optionKeyField: 'customerId',
      initialOptions: this.alarm?.sharingCustomers?.map(({ id, name }) => ({ customerId: id, customerName: name })) ?? [],
      requester: (term: string) => {
        return this.notificationsDatasource.getCustomersWithSharesOptions(term);
      }
    });
    this.customerOptionLoader.load('');
  }

  private patchFormToEdit(): void {
    const hasTimefenceConf = Boolean(this.alarm?.timefenceConfiguration);
    const isTelematicsAlarm = this.alarm?.alarmType === AlarmType.INACTIVE_TELEMATICS_UNIT;

    const formValue = {
      [this.controlNames.ALARM_TYPE]: this.alarm?.alarmType,
      [this.controlNames.SETTINGS]: {
        [this.controlNames.SETTINGS_LIMIT]: this.alarm?.limit,
        [this.controlNames.SETTINGS_LIMIT_UNIT]: this.alarm?.limitDurationUnit,
        [this.controlNames.SETTINGS_INTERVAL]: this.alarm?.timeframeInDays,
        [this.controlNames.SETTINGS_GLOBAL_GEOFENCE]: this.alarm?.globalGeofenceId,
        [this.controlNames.SETTINGS_GEOFENCE_TRIGGER]: this.alarm?.geofenceAlarmTrigger,
        [this.controlNames.SETTINGS_TIMEFENCING_HOURS]: hasTimefenceConf
          ? this.alarm?.timefenceConfiguration?.operatingHoursLimit
          : null,
        [this.controlNames.SETTINGS_TIMEFENCING_DISTANCE]: hasTimefenceConf
          ? this.dimensionSystemService.toUserMeasure(this.alarm?.timefenceConfiguration?.distanceLimit, 'm')
          : null,
        [this.controlNames.SETTINGS_TIMEFENCING_WEEK_DAY]: hasTimefenceConf
          ? this.alarm?.timefenceConfiguration?.weekDayConfiguration
          : null,
        [this.controlNames.SETTINGS_TELEMATICS_TYPES]: isTelematicsAlarm ? this.alarm?.telematicsUnitTypes : [],
        [this.controlNames.SETTINGS_TELEMATICS_SELECTOR]: this.getTelematicSelector(isTelematicsAlarm),
        [this.controlNames.SETTINGS_TELEMATICS_BATTERY_STATUS]: this.alarm?.batteryStatus,
      },
      [this.controlNames.DATA]: {
        [this.controlNames.DATA_ALARM_MESSAGE]: this.alarm?.alarmMessage,
        [this.controlNames.DATA_ALARM_LEVEL]: this.alarm?.alarmLevel,
        [this.controlNames.DATA_SELECTION_TYPE]: this.alarm?.selectionType,
        [this.controlNames.DATA_LABELS]: this.alarm?.labels ?? null,
        // Patching of Equipment Types happens in "patchEquipmentTypesOnInit" due to a specific approach of processing
        [this.controlNames.DATA_EQUIPMENT_TYPES]: null,
        [this.controlNames.DATA_EQUIPMENTS]: this.alarm?.equipments?.map(({ id }) => id),
        [this.controlNames.DATA_CUSTOMER]: this.alarm?.sharingCustomers?.map(({ id }) => id),
        [this.controlNames.DATA_EMAIL_RECIPIENTS]: this.alarm?.emailRecipientsFilledWithInfo
          ? this.alarm?.emailRecipientsFilledWithInfo.map(({ userId }) => userId)
          : null,
        [this.controlNames.DATA_DEFAULT_EMAIL_RECIPIENTS]: this.alarm?.defaultEmailRecipientsFilledWithInfo
          ? this.alarm?.defaultEmailRecipientsFilledWithInfo.map(({ userId }) => userId)
          : null,
        [this.controlNames.DATA_ROLE_RECIPIENTS]: this.alarm?.userRolesEmailRecipientsFilledWithInfo
          ? this.alarm?.userRolesEmailRecipientsFilledWithInfo.map(({ roleId }) => roleId)
          : null,
      },
    };

    this.form.patchValue(formValue);
  }

  private patchEquipmentTypesOnInit(): void {
    const selectedMergedIds = this.equipmentTypeMergeService.findOptionIdsByOriginal(
      this.$equipmentTypes(),
      this.alarm?.equipmentTypeIds ?? [],
    );
    this.form
      .get([AlarmRuleControl.DATA, AlarmRuleControl.DATA_EQUIPMENT_TYPES])
      ?.patchValue(selectedMergedIds);
  }

  private getTelematicSelector(isTelematicsAlarm: boolean): TelematicsSelector | null {
    if (!isTelematicsAlarm) {
      return null;
    }

    if (this.alarm?.telematicsUnitTypes.length === 0) {
      return TelematicsSelector.ALL;
    }

    if (this.isOnlyTelematicsUnitsWithoutBeacons()) {
      return TelematicsSelector.ALL_TELEMATIC_UNITS_EXCEPT_BEACONS;
    }

    return TelematicsSelector.SELECTED;
  }

  private isOnlyTelematicsUnitsWithoutBeacons(): boolean {
    const telematicsUnitTypes = this.alarm?.telematicsUnitTypes;
    const telematicsUnitTypesWithBeacons = telematicsUnitTypes?.filter(
      (item) => !TELEMATICS_UNITS_TYPES_WITHOUT_BEACONS.has(item),
    );
    return (
      telematicsUnitTypesWithBeacons?.length === 0 &&
      telematicsUnitTypes?.length === TELEMATICS_UNITS_TYPES_WITHOUT_BEACONS.size
    );
  }

  private alarmTypeChanged(type: AlarmType): void {
    this.alarmSettingsUtilsService.alarmType = type;
    this.$currentAlarmType.set(type);
    this.$currentSettingsType.set(this.alarmSettingsUtilsService.getAlarmSettingsType());

    if (this.settingsForm) {
      this.updateValidators();
    }
  }

  private updateValidators(): void {
    [
      this.controlNames.SETTINGS_LIMIT,
      this.controlNames.SETTINGS_LIMIT_UNIT,
      this.controlNames.SETTINGS_INTERVAL,
      this.controlNames.SETTINGS_GLOBAL_GEOFENCE,
      this.controlNames.SETTINGS_GEOFENCE_TRIGGER,
      this.controlNames.SETTINGS_TIMEFENCING_WEEK_DAY,
      this.controlNames.SETTINGS_TIMEFENCING_HOURS,
      this.controlNames.SETTINGS_TIMEFENCING_DISTANCE,
      this.controlNames.SETTINGS_TELEMATICS_SELECTOR,
      this.controlNames.SETTINGS_TELEMATICS_TYPES,
    ].forEach((controlName) => this.setValidator(controlName));
  }

  private setValidator(controlName: string): void {
    const control = this.settingsForm?.get(controlName);
    control?.setValidators(this.alarmSettingsUtilsService.getValidators(controlName));
    control?.updateValueAndValidity();
  }

  private createAlarmRule(): void {
    const customerId = this.authService.userCustomerId;
    if (!customerId) return;

    const formData = this.form.getRawValue();
    const command = {
      customerId,
      zoneId: DatesService.getLocalTimeZone(),
    } as CreateAlarmRuleCommand;

    this.setCommonCommandValues(command, formData as FormData);

    this.$isSubmittingForm.set(true);

    this.notificationsDatasource
      .createAlarmRule(command)
      .pipe(
        delay(environment.DELAY_LONG),
        finalize(() => this.$isSubmittingForm.set(false)),
        tap(() => this.notificationsDatasource.updateData()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.closeDialog();
          this.toastService.showSuccess({
            summary: this.translate('general.alarms.alarmRuleSuccesfullyCreated'),
          });
        },
        // error: (error) => {
        //   this.toastService.showError({
        //     summary: this.translate('general.alarms.couldNotCreateAlarmRule'),
        //     detail: error,
        //   });
        // },
      });
  }

  private updateAlarmRule(): void {
    const formData = this.form.getRawValue();
    const command = {
      alarmRuleId: this.alarm?.alarmRuleId,
    } as UpdateAlarmRuleCommand;

    this.setCommonCommandValues(command, formData as FormData);
    this.$isSubmittingForm.set(true);

    this.notificationsDatasource
      .updateAlarmRule(command)
      .pipe(
        delay(environment.DELAY_LONG),
        tap(() => this.notificationsDatasource.updateData()),
        finalize(() => this.$isSubmittingForm.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.closeDialog();
          this.toastService.showSuccess({
            summary: this.translate('general.alarms.alarmRuleSuccesfullyUpdated'),
          });
        },
        // error: (error) => {
        //   this.toastService.showError({
        //     summary: this.translate('general.alarms.couldNotUpdateAlarmRule'),
        //     detail: error?.message ?? error,
        //   });
        // },
      });
  }

  private setCommonCommandValues(cmd: CreateAlarmRuleCommand | UpdateAlarmRuleCommand, formData: FormData): void {
    const settings = formData[this.controlNames.SETTINGS];
    const data = formData[this.controlNames.DATA];

    cmd.alarmType = formData[this.controlNames.ALARM_TYPE];
    cmd.limit = settings[this.controlNames.SETTINGS_LIMIT];
    cmd.limitDurationUnit = settings[this.controlNames.SETTINGS_LIMIT_UNIT];
    cmd.timeframeInDays = settings[this.controlNames.SETTINGS_INTERVAL];
    cmd.globalGeofenceId = settings[this.controlNames.SETTINGS_GLOBAL_GEOFENCE];
    cmd.geofenceAlarmTrigger = settings[this.controlNames.SETTINGS_GEOFENCE_TRIGGER];
    cmd.alarmMessage = data[this.controlNames.DATA_ALARM_MESSAGE];
    cmd.alarmLevel = data[this.controlNames.DATA_ALARM_LEVEL];

    if (this.$currentSettingsType() === AlarmSettingsType.TIMEFENCING) {
      const distanceLimit = this.dimensionSystemService.toSystemMeasure(
        settings[this.controlNames.SETTINGS_TIMEFENCING_DISTANCE],
        'm',
      );
      cmd.timefenceConfiguration = {
        weekDayConfiguration: settings[this.controlNames.SETTINGS_TIMEFENCING_WEEK_DAY],
      };
      cmd.timefenceConfiguration.operatingHoursLimit = settings[this.controlNames.SETTINGS_TIMEFENCING_HOURS];
      cmd.timefenceConfiguration.distanceLimit = distanceLimit > 0 ? distanceLimit : undefined;
    }

    if (this.$currentSettingsType() === AlarmSettingsType.TELEMATICS_UNIT) {
      cmd.telematicsUnitTypes = settings[this.controlNames.SETTINGS_TELEMATICS_TYPES];
    }

    if (this.$currentSettingsType() === AlarmSettingsType.TRACKER_BATTERY_STATUS) {
      cmd.batteryStatus = settings[this.controlNames.SETTINGS_TELEMATICS_BATTERY_STATUS];
    }

    if (formData[this.controlNames.DATA][this.controlNames.DATA_EMAIL_TOGGLE]) {
      cmd.emailRecipients = data[this.controlNames.DATA_EMAIL_RECIPIENTS];
      cmd.userRolesEmailRecipients = data[this.controlNames.DATA_ROLE_RECIPIENTS];
    }

    cmd.defaultEmailRecipients = data[this.controlNames.DATA_DEFAULT_EMAIL_RECIPIENTS];

    cmd.selectionType = data[this.controlNames.DATA_SELECTION_TYPE];
    if (cmd.selectionType === EquipmentAlarmRuleSelectionType.LABEL) {
      cmd.labels = data[this.controlNames.DATA_LABELS];
    } else if (cmd.selectionType === EquipmentAlarmRuleSelectionType.EQUIPMENT_TYPE) {
      cmd.equipmentTypeIds = this.equipmentTypeMergeService.getOriginalIds(data[this.controlNames.DATA_EQUIPMENT_TYPES]);
    } else if (cmd.selectionType === EquipmentAlarmRuleSelectionType.EQUIPMENT) {
      cmd.equipmentIds = data[this.controlNames.DATA_EQUIPMENTS];
    } else if (cmd.selectionType === EquipmentAlarmRuleSelectionType.CUSTOMER) {
      cmd.sharingCustomerIds = data[this.controlNames.DATA_CUSTOMER];
    }
  }

  private fetchTypeConfigs() {
    this.notificationApiService.getAlarmTypeSettings().subscribe( result => {
        this.alarmTypeConfigs = result;
      }
    );
  }

  private translate(key: string): string {
    return this.languageService.getInstant(key);
  }
}
